import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { TAG_LABELS } from '@/components/SentimentTagPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CommunityTagsSummaryChip — Round 84k Phase 2.
 *
 * Slot in the stock modal header that replaces the old "via Yahoo /
 * Massive" debug pill. Shows the top 2 structured sentiment tags
 * the community has applied to posts about this ticker in the last
 * 90 days. Empty state shows a quiet "No community tags yet" hint
 * — it never falls back to nothing so the header layout stays
 * predictable.
 *
 * Powered by the public GET /api/community/tickers/{ticker}/tags-summary.
 * No auth needed — community sentiment is public signal.
 */
export default function CommunityTagsSummaryChip({ ticker, onClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/community/tickers/${encodeURIComponent(ticker)}/tags-summary`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) {
    return (
      <span
        className="text-[9px] px-1.5 py-0.5 rounded font-semibold bg-muted/40 text-muted-foreground"
        data-testid="community-tags-summary-loading"
      >
        Loading sentiment…
      </span>
    );
  }
  const counts = data?.tag_counts || {};
  // Sort by vote count desc, then pick the top 2 for the inline chip
  const sorted = Object.entries(counts)
    .filter(([, n]) => Number(n) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  const total = sorted.reduce((sum, [, n]) => sum + Number(n), 0);

  if (total === 0) {
    const postCount = Number(data?.post_count_90d || 0);
    const label = postCount > 0
      ? `💬 ${postCount} post${postCount === 1 ? '' : 's'} · be first to tag`
      : 'No tags yet';
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-semibold bg-muted/40 text-muted-foreground hover:bg-muted/60 transition-colors"
        title={postCount > 0
          ? `${postCount} community post${postCount === 1 ? '' : 's'} about this ticker in the last 90 days — no structured tags yet. Click to read and add one.`
          : 'No community sentiment tags yet — click to discuss this ticker in the Community'}
        data-testid="community-tags-summary-empty"
      >
        {postCount === 0 && <Users className="w-2.5 h-2.5" />}
        {label}
      </button>
    );
  }

  const top = sorted.slice(0, 2);
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-violet-100/60 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 border border-violet-300/50 dark:border-violet-700/50 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors"
      title={`Community sentiment tags over last 90 days · ${total} vote${total === 1 ? '' : 's'} · click to discuss in the community`}
      data-testid="community-tags-summary-chip"
    >
      <Users className="w-2.5 h-2.5 opacity-80" />
      {top.map(([tag, n]) => {
        const meta = TAG_LABELS[tag];
        if (!meta) return null;
        return (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5"
            data-testid={`community-tag-${tag}`}
          >
            <span>{meta.emoji}</span>
            <span className="hidden sm:inline">{meta.label}</span>
            <span className="font-mono">{n}</span>
          </span>
        );
      })}
      {sorted.length > 2 && (
        <span className="opacity-70">· +{sorted.length - 2} more</span>
      )}
    </button>
  );
}
